/**
 * PodDimensionsWhitelistSelector — whitelist des dimensions POD print
 * proposées dans le dropdown "Format" à la création d'une variante POD
 * open edition. Original/limited gardent l'accès libre au catalogue
 * formats entier (atelier hand-print hors Gelato).
 *
 * Première sauvegarde : si le setting est absent (value vide), le BE
 * retourne `defaults_resolved` (12 dimensions standards matchées contre
 * le catalogue courant) et la UI pré-coche ces format_ids. L'utilisateur
 * doit cliquer "Enregistrer" pour persister.
 *
 * Pas de réordonnancement (ordre arbitraire) — affichage sorted par
 * width_cm ASC. Sémantique additive : aucune vue/listing existant
 * n'est invalidé par un changement de whitelist, seules les nouvelles
 * créations de variantes POD print sont filtrées.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import { useFormats } from '@/hooks/useCatalog'
import {
  usePodDimensionsWhitelist,
  useUpdatePodDimensionsWhitelist,
} from '@/hooks/useAtelierSettings'

import './PodDimensionsWhitelistSelector.css'

export default function PodDimensionsWhitelistSelector() {
  const settingQuery = usePodDimensionsWhitelist()
  const formatsQuery = useFormats({ only_active: true })
  const updateMutation = useUpdatePodDimensionsWhitelist()

  const serverData = settingQuery.data
  const formats = formatsQuery.data ?? []

  // Adjust-state-on-prop-change : resync local state quand le serveur
  // renvoie une nouvelle response (post-save, ou first load).
  const computeInitialSelection = (): Set<number> => {
    if (!serverData) return new Set()
    // Liste vide côté serveur → pré-coche les defaults_resolved (1er chargement).
    if (serverData.value.length === 0) {
      return new Set(serverData.defaults_resolved)
    }
    return new Set(serverData.value)
  }

  const [selected, setSelected] = useState<Set<number>>(computeInitialSelection)
  const [serverSnapshot, setServerSnapshot] = useState<string | null>(
    serverData
      ? JSON.stringify({ value: serverData.value, defaults: serverData.defaults_resolved })
      : null,
  )
  if (serverData) {
    const next = JSON.stringify({
      value: serverData.value,
      defaults: serverData.defaults_resolved,
    })
    if (next !== serverSnapshot) {
      setServerSnapshot(next)
      setSelected(computeInitialSelection())
    }
  }

  const toggle = (formatId: number) => {
    const next = new Set(selected)
    if (next.has(formatId)) {
      next.delete(formatId)
    } else {
      next.add(formatId)
    }
    setSelected(next)
  }

  const handleSave = () => {
    updateMutation.mutate(
      { value: Array.from(selected) },
      {
        onSuccess: (data) => {
          toast.success(
            `Whitelist POD enregistrée (${data.value.length} dimension${data.value.length > 1 ? 's' : ''})`,
          )
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Sauvegarde impossible')
        },
      },
    )
  }

  const isLoading = settingQuery.isLoading || formatsQuery.isLoading

  // Sort active formats by width_cm ASC then height_cm ASC for stable display.
  const sortedFormats = [...formats].sort((a, b) => {
    const wa = Number(a.width_cm)
    const wb = Number(b.width_cm)
    if (wa !== wb) return wa - wb
    return Number(a.height_cm) - Number(b.height_cm)
  })

  const isDirty =
    serverData !== undefined &&
    JSON.stringify(Array.from(selected).sort()) !==
      JSON.stringify(
        (serverData.value.length === 0 ? serverData.defaults_resolved : serverData.value)
          .slice()
          .sort(),
      )

  return (
    <div>
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Dimensions POD print (Etsy)</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Seules ces dimensions seront proposées dans le dropdown <strong>Format</strong> à la
            création d&apos;une variante <strong>POD open edition</strong> (impression Gelato). Les
            variantes <em>original</em> et <em>limited</em> (atelier hand-print) gardent
            l&apos;accès libre au catalogue formats entier.
          </p>
          <p className="ui-body-sm ui-text-muted settings-section-subtitle">
            Au 1er chargement, 12 dimensions standards sont pré-cochées (A5/A4/A3/A2/50×70 +
            8×10&quot;/11×14&quot;/16×20&quot;/18×24&quot; + 30×40 cm / 24×36&quot; / 13×18 cm).
            Édite freely et sauvegarde — la liste devient ta source de vérité. Sauvegarder une liste
            vide réactive les défauts.
          </p>
        </div>
      </header>

      <div className="atelier-shipping-fieldset">
        {isLoading ? (
          <p className="ui-text-muted">Chargement…</p>
        ) : sortedFormats.length === 0 ? (
          <p className="ui-body ui-text-muted">
            Aucun format actif dans le catalogue — crée-en via l&apos;onglet{' '}
            <strong>Formats</strong> avant de configurer la whitelist.
          </p>
        ) : (
          <div className="pod-dimensions-whitelist-grid">
            {sortedFormats.map((fmt) => {
              const checked = selected.has(fmt.id)
              return (
                <label
                  key={fmt.id}
                  className={`pod-dimensions-whitelist-row${checked ? ' pod-dimensions-whitelist-row--checked' : ''}`}
                >
                  <Checkbox checked={checked} onChange={() => toggle(fmt.id)} />
                  <span className="ui-body pod-dimensions-whitelist-label">{fmt.label_fr}</span>
                  <span className="ui-body-sm ui-text-muted pod-dimensions-whitelist-dim">
                    {Number(fmt.width_cm).toFixed(1)} × {Number(fmt.height_cm).toFixed(1)} cm
                  </span>
                </label>
              )
            })}
          </div>
        )}

        <div className="atelier-shipping-actions">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Sauvegarde…' : 'Enregistrer la whitelist'}
          </Button>
          {isDirty && (
            <span className="ui-text-muted ui-body-sm">Modifications non enregistrées</span>
          )}
        </div>
      </div>
    </div>
  )
}
